import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signUp, logIn, signInWithGoogle } from '../../auth';
import './style.css';
import { EyeIcon, EyeSlashIcon } from '../../assets/icons/icons';
import { useBrand } from '../../branding/BrandProvider';

export default function Login() {
    const [view, setView] = useState('intro');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [signupNotice, setSignupNotice] = useState('');
    const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
    const [installFeedback, setInstallFeedback] = useState('');
    const [isIosDevice, setIsIosDevice] = useState(false);
    const [isStandalone, setIsStandalone] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    });
    const { name: brandName, logos } = useBrand();
    const brandLogo = logos?.fullLarge || logos?.full || logos?.fullWhite;
    const installLogo = `${process.env.PUBLIC_URL}/logo512.png`;
    const brandAlt = brandName || 'Logo';

    const navigate = useNavigate();
    const isSigningUp = view === 'signup';
    const isLoggingIn = view === 'login';

    useEffect(() => {
        if (!isSigningUp) {
            setConfirmPassword('');
            setSignupNotice('');
        }
    }, [isSigningUp]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const standaloneMediaQuery = window.matchMedia('(display-mode: standalone)');
        const syncStandaloneState = () => {
            setIsStandalone(
                standaloneMediaQuery.matches || window.navigator.standalone === true
            );
        };
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIos = /iphone|ipad|ipod/.test(userAgent);
        setIsIosDevice(isIos);
        syncStandaloneState();

        const handleBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setDeferredInstallPrompt(event);
            setInstallFeedback('');
        };

        const handleAppInstalled = () => {
            setDeferredInstallPrompt(null);
            setIsStandalone(true);
            setInstallFeedback('Added to your home screen.');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);
        if (standaloneMediaQuery.addEventListener) {
            standaloneMediaQuery.addEventListener('change', syncStandaloneState);
        } else {
            standaloneMediaQuery.addListener(syncStandaloneState);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
            if (standaloneMediaQuery.removeEventListener) {
                standaloneMediaQuery.removeEventListener('change', syncStandaloneState);
            } else {
                standaloneMediaQuery.removeListener(syncStandaloneState);
            }
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSignupNotice('');

        try {
            if (isSigningUp) {
                if (password !== confirmPassword) {
                    setError("Passwords don't match");
                    return;
                }
                const { session } = await signUp(email, password);
                if (session) {
                    navigate('/select-plan');
                    return;
                }
                try {
                    await logIn(email, password);
                    navigate('/select-plan');
                } catch (loginError) {
                    const message = loginError?.message?.toLowerCase().includes('confirm')
                        ? 'Please confirm your email to continue.'
                        : 'Account created. Please sign in to continue.';
                    setSignupNotice(message);
                }
                return;
            }

            if (isLoggingIn) {
                await logIn(email, password);
                navigate('/');
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            await signInWithGoogle();
            navigate('/dashboard');
        } catch (error) {
            setError(error.message);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };

    const handleInstallCardClick = async () => {
        if (isStandalone) return;

        if (deferredInstallPrompt) {
            deferredInstallPrompt.prompt();
            const { outcome } = await deferredInstallPrompt.userChoice;
            if (outcome === 'accepted') {
                setInstallFeedback('Installing app...');
            } else {
                setInstallFeedback('Install dismissed. You can add it anytime.');
            }
            setDeferredInstallPrompt(null);
            return;
        }

        if (isIosDevice) {
            setInstallFeedback("On iOS, tap Share then 'Add to Home Screen'.");
            return;
        }

        setInstallFeedback('Install option not ready yet. Refresh and try again in Chrome or Safari.');
    };

    const showInstallCard = (isLoggingIn || isSigningUp) && !isStandalone;

    return (
        <div className="login-screen">
            <div className="modal-content login-modal">
                <div className="login-logo" aria-hidden>
                    {brandLogo && <img src={brandLogo} alt={brandAlt} className="login-logo-img" />}
                </div>

                {view === 'intro' && (
                    <>
                        <h1 className="login-title">Ready to Get Started?</h1>
                        <p className="login-subtitle">Start your transformation with ColeDitioned Coaching.</p>
                        <button
                            type="button"
                            className="login-button"
                            onClick={() => setView('signup')}
                        >
                            Start my Transformation
                        </button>
                        <a
                            href="#login"
                            className="login-link login-link-inline"
                            onClick={(event) => {
                                event.preventDefault();
                                setView('login');
                            }}
                        >
                            Need to login?
                        </a>
                        <div className="login-legal">
                            <a className='login-link' href='/privacy-policy'>Privacy Policy</a>
                            <a className='login-link' href='/terms-of-service'>Terms of Service</a>
                        </div>
                    </>
                )}

                {(view === 'login' || view === 'signup') && (
                    <>
                        <h1 className="login-title">
                            {isSigningUp ? 'First Things First' : 'Welcome Back'}
                        </h1>

                        {showInstallCard && (
                            <button
                                type="button"
                                className="pwa-install-card"
                                onClick={handleInstallCardClick}
                            >
                                <span className="pwa-install-copy">
                                    <span className="pwa-install-title">Install the app</span>
                                    <span className="pwa-install-subtitle">Add to homescreen for best experience</span>
                                </span>
                                <span className="pwa-install-badge" aria-hidden>
                                    {installLogo && (
                                        <img
                                            src={installLogo}
                                            alt=""
                                            className="pwa-install-badge-logo"
                                        />
                                    )}
                                </span>
                            </button>
                        )}
                        {installFeedback && <p className="pwa-install-feedback">{installFeedback}</p>}

                        {error && <p className="error-message">{error}</p>}

                        {isSigningUp && signupNotice && (
                            <p className="success-message">{signupNotice}</p>
                        )}

                        <form onSubmit={handleSubmit}>
                            <label className="login-label">Email</label>
                            <input
                                type="email"
                                value={email}
                                id="email"
                                name="email"
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email address"
                                autoComplete="email"
                                required
                                className='login-input'
                            />

                            <label className="login-label">Password</label>
                            <div className="password-input-container">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    id="password"
                                    name="password"
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Password"
                                    autoComplete={isSigningUp ? 'new-password' : 'current-password'}
                                    required
                                    className='login-input'
                                />
                                <span className="password-toggle-icon" onClick={togglePasswordVisibility}>
                                    {!showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                </span>
                            </div>

                            {isSigningUp && (
                                <>
                                    <label className="login-label">Confirm Password</label>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        id="confirm-password"
                                        name="confirm-password"
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Re-enter password"
                                        autoComplete="new-password"
                                        required={isSigningUp}
                                        className='login-input'
                                    />
                                </>
                            )}

                            <button type="submit" className="login-button">
                                {isSigningUp ? 'Next' : 'Sign in'}
                            </button>
                        </form>

                        {/* <button onClick={handleGoogleLogin} className="google-button">
                            <GoogleLogo className="google-icon" />
                            Continue with Google
                        </button> */}

                        <div className="toggle-auth">
                            {isSigningUp ? (
                                <p>
                                    Need to login?{' '}
                                    <span onClick={() => setView('login')}>Sign in</span>
                                </p>
                            ) : (
                                <p>
                                    New here?{' '}
                                    <span onClick={() => setView('signup')}>Start my transformation</span>
                                </p>
                            )}
                            <a className='login-link' href='/privacy-policy'>Privacy Policy</a>
                            <a className='login-link' href='/terms-of-service'>Terms of Service</a>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
